import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { AlertTriangle, ShieldCheck, RefreshCw, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import SnapshotRefresh from '../components/SnapshotRefresh';

const SEVERITY = {
  high:   { label: 'High',   pill: 'cb-pill--negative', dotColor: 'var(--cb-negative)' },
  medium: { label: 'Medium', pill: 'cb-pill--negative', dotColor: 'var(--cb-mark)' },
  low:    { label: 'Low',    pill: '',                  dotColor: 'var(--cb-ink-soft)' },
};

const fmt = (v) => v === null || v === undefined ? '—' : (typeof v === 'number' ? v.toLocaleString('en-IN') : String(v));

const IssueGroup = ({ group }) => {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY[group.severity] || SEVERITY.low;

  return (
    <div
      className="cb-card mb-4"
      style={{ padding: 0, overflow: 'hidden' }}
      data-testid={`dq-group-${group.type}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center text-left"
        style={{ gap: 'var(--cb-space-3)', padding: 'var(--cb-space-4) var(--cb-space-5)', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink)' }}
        data-testid={`dq-toggle-${group.type}`}
      >
        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '999px', background: sev.dotColor, flexShrink: 0 }} />
        <div className="flex-1">
          <div className="flex items-center" style={{ gap: 'var(--cb-space-3)' }}>
            <span style={{ fontWeight: 500, color: 'var(--cb-ink)' }}>{group.title}</span>
            <span className="cb-pill cb-num" data-testid={`dq-count-${group.type}`}>
              {group.count} affected
            </span>
            <span className={`cb-pill ${sev.pill}`}>{sev.label}</span>
          </div>
          <p className="cb-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>{group.description}</p>
        </div>
        {open ? <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--cb-ink-soft)' }} /> : <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--cb-ink-soft)' }} />}
      </button>
      {open && group.examples?.length > 0 && (
        <div style={{ borderTop: '1px solid var(--cb-line)', padding: 'var(--cb-space-3) var(--cb-space-5)' }} data-testid={`dq-examples-${group.type}`}>
          <p className="cb-card-label" style={{ marginBottom: 'var(--cb-space-2)' }}>
            Showing {group.examples.length} of {group.count}
          </p>
          <div className="overflow-x-auto">
            <table className="cb-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  {Object.keys(group.examples[0]).map((k) => (
                    <th key={k}>{k.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.examples.map((ex, i) => (
                  <tr key={i}>
                    {Object.values(ex).map((v, j) => (
                      <td key={j} className="cb-num" style={{ fontSize: '0.75rem' }}>{fmt(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const DataQuality = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/data-quality');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data quality report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center cb-muted" style={{ gap: 'var(--cb-space-3)' }}>
        <RefreshCw className="w-4 h-4 animate-spin" /> Scanning the ledger…
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="data-quality-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center mb-4"
        style={{ gap: 'var(--cb-space-2)', fontSize: '0.8125rem', color: 'var(--cb-ink-soft)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
        data-testid="dq-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="cb-page-header">
        <div className="flex items-start justify-between" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Integrity</p>
            <div className="flex items-center" style={{ gap: 'var(--cb-space-3)' }}>
              {data?.total_issues === 0 ? (
                <ShieldCheck className="w-8 h-8" style={{ color: 'var(--cb-positive)' }} />
              ) : (
                <AlertTriangle className="w-8 h-8" style={{ color: 'var(--cb-mark)' }} />
              )}
              <h1 className="cb-h1">Data Quality</h1>
            </div>
            <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)' }}>
              Automated audit of ledger integrity, balance drift, and approval health.
              {data?.checked_at && <> Last scanned {new Date(data.checked_at).toLocaleString('en-IN')}.</>}
            </p>
          </div>
          <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
            <SnapshotRefresh />
            <button
              onClick={fetchReport}
              disabled={loading}
              className="cb-btn-primary"
              data-testid="dq-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Scanning…' : 'Re-scan'}
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {data?.total_issues === 0 ? (
        <div
          className="cb-card text-center"
          style={{ padding: 'var(--cb-space-9) var(--cb-space-5)' }}
          data-testid="dq-clean"
        >
          <ShieldCheck className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--cb-positive)' }} />
          <h2 className="cb-section-title">All clean</h2>
          <p className="cb-muted" style={{ marginTop: 'var(--cb-space-2)' }}>
            No data integrity issues detected across ledger, cash pool, approvals, or partner attribution.
          </p>
        </div>
      ) : (
        <>
          <div className="cb-card flex items-center mb-6" style={{ gap: 'var(--cb-space-6)' }} data-testid="dq-summary">
            <div>
              <p className="cb-card-label">Total issues</p>
              <p className="cb-card-value cb-num">{data?.total_issues}</p>
            </div>
            <div style={{ borderLeft: '1px solid var(--cb-line)', height: '3rem' }} />
            <div className="flex" style={{ gap: 'var(--cb-space-5)', fontSize: '0.875rem' }}>
              {['high', 'medium', 'low'].map((s) => {
                const total = (data?.issue_groups || []).filter((g) => g.severity === s).reduce((sum, g) => sum + g.count, 0);
                if (!total) return null;
                const sev = SEVERITY[s];
                return (
                  <div key={s} className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '999px', background: sev.dotColor }} />
                    <span className="cb-muted">{sev.label}: <span className="cb-num" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>{total}</span></span>
                  </div>
                );
              })}
            </div>
          </div>

          {data?.issue_groups?.map((g) => <IssueGroup key={g.type} group={g} />)}
        </>
      )}
    </div>
  );
};

export default DataQuality;
